import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { useLocation } from 'react-router-dom'
import { ROTATION_SPEED, figureScreenX, poseFor, type StagePose } from './framing'
import './hud.css'

/**
 * Die Instrumenten-Ebene über der Bühne.
 *
 * Der 4NE-1 stand bisher als Standbild im Hintergrund — technisch sauber,
 * aber leblos. Diese Ebene macht aus dem Hintergrund ein Instrument: Ringe um
 * die Figur, Eckklammern am Bildrand, ein feines Raster, laufende Messwerte.
 *
 * Drei Entscheidungen, die den Unterschied machen:
 *
 * 1. SVG und CSS, NICHT Three.js. Ringe im Bildschirmraum bleiben bei jeder
 *    Pixeldichte scharf, kosten praktisch nichts und stehen sofort — auch
 *    solange der Three-Chunk noch lädt oder gar kein WebGL da ist.
 *
 * 2. Die Ringe zentrieren auf DEN PUNKT, an dem die Figur wirklich steht.
 *    Der kommt aus derselben Formel wie der Kameraversatz (framing.ts). Ein
 *    fest gesetzter Prozentwert würde bei jeder Fensterbreite danebenliegen.
 *
 * 3. Alles bleibt LICHT an Kanten. Kein Ring wird zur Farbfläche, keine
 *    Messwertspalte zum Kasten. Sonst kippt die Marke von „monochrom mit
 *    Lichtakzent" in „blaues Sci-Fi-Theme".
 *
 * Reine Schau: keine Zeigerereignisse, für Screenreader unsichtbar, bei
 * `prefers-reduced-motion` steht alles still.
 */

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setReduced(media.matches)
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])

  return reduced
}

/** Die Stelle der Figur als Anteil der Fensterbreite. */
function useFigureScreenX(pose: StagePose): number {
  const [aspect, setAspect] = useState(() =>
    typeof window === 'undefined' ? 16 / 9 : window.innerWidth / Math.max(window.innerHeight, 1),
  )

  useEffect(() => {
    const update = () => setAspect(window.innerWidth / Math.max(window.innerHeight, 1))
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  return figureScreenX(aspect, pose)
}

/**
 * Beim Wechsel des Moduls rastet das Instrument neu ein.
 *
 * Waehrend die Kamera zur neuen Pose faehrt, laeuft am HUD eine kurze
 * Neukalibrierung. Ohne sie zieht der Ringcluster stumm auf eine andere Groesse
 * — technisch richtig, aber es liest sich als Ruckler statt als Absicht.
 */
function usePoseShift(poseKey: string, enabled: boolean): boolean {
  const [shifting, setShifting] = useState(false)
  const previous = useRef(poseKey)

  useEffect(() => {
    if (!enabled) return
    if (previous.current === poseKey) return
    previous.current = poseKey
    setShifting(true)
    const timer = window.setTimeout(() => setShifting(false), 900)
    return () => window.clearTimeout(timer)
  }, [poseKey, enabled])

  return shifting
}

/**
 * Der mitlaufende Winkel der Drehung.
 *
 * Bewusst über eine Referenz direkt in den Textknoten geschrieben statt über
 * State: ein Messwert, der sich sechsmal je Sekunde ändert, darf nicht
 * sechsmal je Sekunde den React-Baum anfassen.
 */
function AzimuthReadout({ animate }: { animate: boolean }) {
  const ref = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    if (!animate) return
    let frame = 0
    let last = 0
    const start = performance.now()

    const tick = (now: number) => {
      // Sechsmal je Sekunde reicht — der Wert soll laufen, nicht flackern.
      if (now - last > 160) {
        last = now
        const deg = (((now - start) / 1000) * ROTATION_SPEED * (180 / Math.PI)) % 360
        if (ref.current) ref.current.textContent = deg.toFixed(1).padStart(5, '0')
      }
      frame = requestAnimationFrame(tick)
    }

    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [animate])

  return <span ref={ref}>000.0</span>
}

/** Ein Ring aus Teilstrichen — dieselbe Teilung wie am Sockel. */
function TickRing({
  r,
  className,
  count = 72,
  duty = 0.16,
}: {
  r: number
  className: string
  count?: number
  duty?: number
}) {
  const period = (2 * Math.PI * r) / count
  return (
    <circle
      className={className}
      cx="0"
      cy="0"
      r={r}
      strokeDasharray={`${(period * duty).toFixed(2)} ${(period * (1 - duty)).toFixed(2)}`}
    />
  )
}

/**
 * Radiale Speichen.
 *
 * Der Zug, der die Vorlagen trägt: nicht der Ring macht die Anmutung, sondern
 * die Striche, die von ihm nach innen zeigen. Über einen gestrichelten Ring
 * mit sehr breiter Strichstärke wäre das billiger, würde aber innen und aussen
 * gleich dick auslaufen — hier soll jeder Strich eine Länge haben.
 */
function Spokes({
  inner,
  outer,
  count,
  className,
}: {
  inner: number
  outer: number
  count: number
  className: string
}) {
  return (
    <g className={className}>
      {Array.from({ length: count }, (_, i) => {
        const a = (i / count) * Math.PI * 2
        const long = i % 6 === 0
        const r1 = long ? inner - 10 : inner
        return (
          <line
            key={i}
            x1={(Math.cos(a) * r1).toFixed(2)}
            y1={(Math.sin(a) * r1).toFixed(2)}
            x2={(Math.cos(a) * outer).toFixed(2)}
            y2={(Math.sin(a) * outer).toFixed(2)}
            opacity={long ? 0.95 : 0.45}
          />
        )
      })}
    </g>
  )
}

/**
 * Der Aufbau laeuft EINMAL je Sitzung, nicht bei jedem Seitenwechsel.
 *
 * Das HUD haengt in der Shell und bleibt beim Navigieren montiert — aber die
 * Bühne wird nachgeladen, und ein Wechsel des Registers baut sie neu auf. Ohne
 * dieses Modul-Flag kalibrierte sich das Instrument bei jedem Klick neu, und
 * aus einer einmaligen Geste würde ein Tick.
 */
let bootShown = false

function useBootOnce(enabled: boolean): boolean {
  const [booting, setBooting] = useState(() => enabled && !bootShown)

  useEffect(() => {
    if (!booting) return
    bootShown = true
    const timer = window.setTimeout(() => setBooting(false), 2000)
    return () => window.clearTimeout(timer)
  }, [booting])

  return booting
}

/**
 * Das Instrument folgt dem Zeiger.
 *
 * Sehr klein gehalten — ein paar Pixel. Es geht nicht darum, dass jemand die
 * Bewegung bemerkt, sondern darum, dass die Ebene nicht wie eine aufgeklebte
 * Grafik wirkt. Dieselbe Parallaxe liegt auf der Kamera der 3D-Szene; beide
 * zusammen ergeben Tiefe.
 */
function usePointerParallax(enabled: boolean) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!enabled) return
    const node = ref.current
    if (!node) return
    if (!window.matchMedia('(pointer: fine)').matches) return

    let raf = 0
    const goal = { x: 0, y: 0 }
    const now = { x: 0, y: 0 }

    const onMove = (event: PointerEvent) => {
      goal.x = (event.clientX / window.innerWidth) * 2 - 1
      goal.y = (event.clientY / window.innerHeight) * 2 - 1
    }

    const draw = () => {
      now.x += (goal.x - now.x) * 0.05
      now.y += (goal.y - now.y) * 0.05
      node.style.setProperty('--hud-shift-x', `${(now.x * 14).toFixed(2)}px`)
      node.style.setProperty('--hud-shift-y', `${(now.y * 10).toFixed(2)}px`)
      raf = requestAnimationFrame(draw)
    }

    window.addEventListener('pointermove', onMove, { passive: true })
    raf = requestAnimationFrame(draw)

    return () => {
      window.removeEventListener('pointermove', onMove)
      cancelAnimationFrame(raf)
    }
  }, [enabled])

  return ref
}

export function Hud() {
  const reduced = usePrefersReducedMotion()
  const animate = !reduced
  const pose = poseFor(useLocation().pathname)
  const screenX = useFigureScreenX(pose)
  const booting = useBootOnce(animate)
  const shifting = usePoseShift(pose.key, animate)
  const ref = usePointerParallax(animate)

  const style = {
    '--hud-x': `${(screenX * 100).toFixed(2)}%`,
    // Der Cluster schrumpft mit der Figur. Als Faktor auf die Grundgroesse,
    // nicht als eigener Wert je Pose: die Grundgroesse haengt am Fenster und
    // soll das auch weiter tun.
    '--hud-pose': pose.hud,
  } as CSSProperties

  const classes = [
    'hud',
    animate ? '' : 'hud--still',
    booting ? 'hud--boot' : '',
    shifting ? 'hud--shift' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div ref={ref} className={classes} style={style} aria-hidden="true" data-testid="hud">
      {/* Feines Raster über der gesamten Fläche. Es liest sich nicht als
          Muster, sondern als Messfeld — man sieht es erst, wenn man es sucht. */}
      <div className="hud__grid" />

      {/* Eckklammern am Bildrand. Vier Winkel, sonst nichts: das ist der
          billigste und wirksamste Griff, um aus einer Seite ein Sichtfeld zu
          machen. */}
      <div className="hud__frame">
        <span className="hud__corner hud__corner--tl" />
        <span className="hud__corner hud__corner--tr" />
        <span className="hud__corner hud__corner--bl" />
        <span className="hud__corner hud__corner--br" />
        {/* Zwei Skalen an den Seitenrändern. Sie tun nichts — aber sie machen
            aus dem Bildrand eine Messkante. */}
        <span className="hud__scale hud__scale--left" />
        <span className="hud__scale hud__scale--right" />
      </div>

      {/* Ringcluster um die Figur */}
      <div className="hud__rings">
        <svg viewBox="-256 -256 512 512" role="presentation" focusable="false">
          {/* Aussenring: drei lange Bögen, langsam mitlaufend */}
          <g className="hud__spin hud__spin--slow">
            <circle
              className="hud__arc"
              cx="0"
              cy="0"
              r="240"
              strokeDasharray="330 172.5"
              strokeDashoffset="40"
            />
          </g>

          {/* Speichenkranz, gegenläufig — gleichsinnige Drehungen wirken wie
              ein Fehler, gegenläufige wie Absicht. Genau wie am Sockel. */}
          <g className="hud__spin hud__spin--reverse">
            <Spokes inner={200} outer={228} count={60} className="hud__spokes" />
            <TickRing r={234} className="hud__ticks" count={120} duty={0.1} />
          </g>

          {/* Vier Bögen mit Lücken auf den Hauptachsen */}
          <g className="hud__spin hud__spin--medium">
            <circle
              className="hud__arc hud__arc--bright"
              cx="0"
              cy="0"
              r="186"
              strokeDasharray="166 41.4"
            />
            {/* Der eine warme Bogen.

                In den Vorlagen ist genau ein Element aus der kalten Reihe
                herausgenommen — das ist es, was den Rest kalt aussehen lässt.
                Der Ton kommt aus der Statusfarbe, nicht aus der Marke: die
                bleibt monochrom mit EINER Lichtfarbe. */}
            <circle
              className="hud__arc hud__arc--warm"
              cx="0"
              cy="0"
              r="172"
              strokeDasharray="118 962"
              strokeDashoffset="-330"
            />
          </g>

          {/* Feiner Speichenkranz weiter innen, gleichsinnig mit dem
              Aussenring. Er ist es, der aus zwei Kreisen ein Instrument
              macht — ein Ringcluster aus blossen Linien bleibt duenn. */}
          <g className="hud__spin hud__spin--slow">
            <Spokes inner={140} outer={152} count={90} className="hud__spokes" />
          </g>

          {/* Segmentring: kurze Boegen mit klaren Luecken. */}
          <g className="hud__spin hud__spin--medium hud__spin--flip">
            <circle
              className="hud__arc"
              cx="0"
              cy="0"
              r="160"
              strokeDasharray="46 22"
              strokeWidth="4"
            />
          </g>

          {/* Ruhender Innenring mit Fadenkreuz — er gibt dem Cluster eine
              Mitte, um die sich alles andere dreht. */}
          <circle className="hud__ring" cx="0" cy="0" r="150" />
          <g className="hud__cross">
            <line x1="0" y1="-166" x2="0" y2="-136" />
            <line x1="0" y1="136" x2="0" y2="166" />
            <line x1="-166" y1="0" x2="-136" y2="0" />
            <line x1="136" y1="0" x2="166" y2="0" />
          </g>

          {/* Zwei kurze Marken, die als Paar laufen — der „Fangring" */}
          <g className="hud__spin hud__spin--fast">
            <circle
              className="hud__arc hud__arc--bright"
              cx="0"
              cy="0"
              r="122"
              strokeDasharray="26 357.3"
            />
          </g>
        </svg>

        {/* Zielklammern um die Figur. Getrennt vom SVG, damit sie am
            Seitenverhältnis der Figur hängen und nicht am Ring. */}
        <span className="hud__lock hud__lock--tl" />
        <span className="hud__lock hud__lock--tr" />
        <span className="hud__lock hud__lock--bl" />
        <span className="hud__lock hud__lock--br" />

        {/* Streifende Abtastung. Eine Kante, die durchläuft — keine Fläche,
            die aufleuchtet. */}
        <span className="hud__sweep" />
      </div>

      {/* Messwerte am Ring. Bewusst wenige und bewusst wahr: was hier steht,
          beschreibt die Bühne, statt technische Daten zu erfinden. */}
      <div className="hud__readout hud__readout--figure">
        <span className="hud__label">Showpiece</span>
        <span className="hud__value numeric">Gen5</span>
        <span className="hud__line" />
        <dl className="hud__data">
          <div>
            <dt>Azimut</dt>
            <dd className="numeric">
              <AzimuthReadout animate={animate} />°
            </dd>
          </div>
          <div>
            <dt>Rate</dt>
            <dd className="numeric">{ROTATION_SPEED.toFixed(3)} rad/s</dd>
          </div>
          <div>
            <dt>Modus</dt>
            <dd className="numeric">{animate ? pose.label : 'Statisch'}</dd>
          </div>
        </dl>
      </div>
    </div>
  )
}
